import { useState } from "react";

/**
 * A chip row that stops growing (#473).
 *
 * Both tag rows in the journal grew without limit -- the filter bar and
 * the chip row on every entry. Twenty tags made the filter bar taller
 * than the first entry and put twenty chips on each card.
 *
 * ONE COMPONENT FOR BOTH, deliberately. Two different ways to pick a tag
 * on one screen is how people learn that one of them does something
 * different, and the filter bar and the entry row are already doing
 * subtly different things (narrowing a list versus filing an entry).
 * They should at least LOOK like the same control.
 *
 * ── A SELECTED CHIP IS NEVER HIDDEN ──────────────────────────────────
 *
 * The obvious implementation -- show the first N -- hides an ACTIVE one
 * the moment somebody has more tags than the limit. That is the worst
 * version of this: the list is narrowed by something with no visible
 * control to turn off, which reads as the journal having lost entries.
 * The same shape as retiring a tag that is still being filtered by,
 * which JournalPage already guards against.
 *
 * So selected chips are pulled to the front and always rendered, and
 * the limit applies to what is left.
 *
 * ── MULTI-SELECT SURVIVES ────────────────────────────────────────────
 *
 * Collapsing into a plain dropdown was the other option and it cannot
 * work here: an entry may carry several tags and a filter may use
 * several, so a single-select control would quietly remove a capability
 * the feature is built on. Expanding in place keeps every chip a
 * toggle.
 */
export function CollapsibleChips({ items, limit = 8, isSelected, renderItem, moreLabel = "more" }) {
  const [expanded, setExpanded] = useState(false);

  const selected = items.filter((i) => isSelected?.(i));
  const rest = items.filter((i) => !isSelected?.(i));

  const hiddenCount = expanded ? 0 : Math.max(rest.length - limit, 0);
  const shown = [...selected, ...(expanded ? rest : rest.slice(0, limit))];

  return (
    <>
      {shown.map(renderItem)}
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="link-button"
          /* Chip-sized, because it sits IN a chip row: the shared class
             supplies the affordance, this keeps the scale. */
          style={{ fontSize: "var(--font-size-sm)", padding: "2px 8px" }}
        >
          +{hiddenCount} {moreLabel}
        </button>
      )}
      {expanded && rest.length > limit && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="link-button"
          /* Chip-sized, because it sits IN a chip row: the shared class
             supplies the affordance, this keeps the scale. */
          style={{ fontSize: "var(--font-size-sm)", padding: "2px 8px" }}
        >
          Show fewer
        </button>
      )}
    </>
  );
}
